'use client';

import * as React from 'react';
import { useParticipants } from '@livekit/components-react';
import { useReactions, Reaction } from './ReactionsContext';

export function ReactionsDisplay() {
  const { reactions } = useReactions();
  const participants = useParticipants();
  const activeReactionsRef = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const reactionsRef = React.useRef(reactions);
  const participantsRef = React.useRef(participants);

  reactionsRef.current = reactions;
  participantsRef.current = participants;

  React.useEffect(() => {
    const displayReactions = () => {
      const currentReactions = reactionsRef.current;
      const currentParticipants = participantsRef.current;
      // Agrupa reações por participante (apenas reações recentes, menos de 5 segundos)
      const now = Date.now();
      const recentReactions = currentReactions.filter((r) => now - r.timestamp < 5000);
      
      const reactionsByParticipant = new Map<string, Reaction[]>();
      
      recentReactions.forEach((reaction) => {
        const existing = reactionsByParticipant.get(reaction.participantId) || [];
        existing.push(reaction);
        reactionsByParticipant.set(reaction.participantId, existing);
      });

      // Remove reações que expiraram
      activeReactionsRef.current.forEach((overlay, participantId) => {
        if (!reactionsByParticipant.has(participantId)) {
          overlay.remove();
          activeReactionsRef.current.delete(participantId);
        }
      });

      // Encontra os tiles dos participantes e exibe as reações
      const tiles = Array.from(
        document.querySelectorAll<HTMLElement>(
          '.lk-participant-tile, .lk-grid-item, .lk-stage-participant, .lk-participant',
        ),
      );

      const nameSelectors = [
        '.lk-participant-name',
        '[class*="participant-name"]',
        '[data-participant-name]',
      ];

      const getParticipantId = (tile: HTMLElement): string | null => {
        // Tenta encontrar pelo atributo data do LiveKit
        const videoElement = tile.querySelector('video');
        if (videoElement) {
          const participantIdentity = videoElement.getAttribute('data-participant-identity');
          if (participantIdentity) return participantIdentity;
        }

        // Tenta encontrar pelo nome
        for (const sel of nameSelectors) {
          const el = tile.querySelector<HTMLElement>(sel);
          if (el && el.textContent) {
            const name = el.textContent.trim();
            // Procura o participante pelo nome ou identidade
            const participant = currentParticipants.find(
              (p) => p.name === name || p.identity === name || name.includes(p.name || ''),
            );
            if (participant) return participant.identity;
          }
        }

        // Fallback: procura por spans pequenos (labels do tile)
        const spans = Array.from(tile.querySelectorAll('span')).filter(
          (s) => (s.textContent || '').trim().length > 0 && s.clientHeight <= 24,
        );
        if (spans[0]?.textContent) {
          const name = spans[0].textContent.trim();
          const participant = currentParticipants.find(
            (p) => p.name === name || p.identity === name || name.includes(p.name || ''),
          );
          if (participant) return participant.identity;
        }

        return null;
      };

      tiles.forEach((tile) => {
        const participantId = getParticipantId(tile);
        if (!participantId) return;

        const participantReactions = reactionsByParticipant.get(participantId);
        if (!participantReactions || participantReactions.length === 0) return;

        // Se já existe uma reação ativa para este participante, não adiciona outra
        if (activeReactionsRef.current.has(participantId)) return;

        // Garante que o tile tem posição relativa
        const computedStyle = window.getComputedStyle(tile);
        if (computedStyle.position === 'static') {
          tile.style.position = 'relative';
        }

        // Pega a última reação (mais recente)
        const latestReaction = participantReactions[participantReactions.length - 1];

        // Cria overlay de reação
        const overlay = document.createElement('div');
        overlay.setAttribute('data-reaction-overlay', 'true');
        overlay.setAttribute('data-participant-id', participantId);
        overlay.textContent = latestReaction.type;
        overlay.style.cssText = `
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) scale(0);
          font-size: 64px;
          pointer-events: none;
          z-index: 1000;
          animation: reactionPop 2s ease-out forwards;
          filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5));
        `;

        tile.appendChild(overlay);
        activeReactionsRef.current.set(participantId, overlay);

        // Remove após animação
        setTimeout(() => {
          if (overlay.parentElement) {
            overlay.remove();
          }
          activeReactionsRef.current.delete(participantId);
        }, 2000);
      });
    };

    displayReactions();

    const root =
      document.querySelector('.lk-room-container') ??
      document.querySelector('.lk-video-conference') ??
      document.body;

    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    const observer = new MutationObserver(() => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(displayReactions, 250);
    });

    observer.observe(root, { childList: true, subtree: true });

    const interval = setInterval(displayReactions, 1_500);

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      observer.disconnect();
      clearInterval(interval);
      activeReactionsRef.current.forEach((overlay) => overlay.remove());
      activeReactionsRef.current.clear();
    };
  }, []);

  // Adiciona estilo de animação se não existir
  React.useEffect(() => {
    if (typeof document === 'undefined') return;

    const styleId = 'reactions-animation-style';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes reactionPop {
        0% {
          transform: translate(-50%, -50%) scale(0) translateY(0);
          opacity: 0;
        }
        10% {
          transform: translate(-50%, -50%) scale(1.2) translateY(-20px);
          opacity: 1;
        }
        30% {
          transform: translate(-50%, -50%) scale(1) translateY(-40px);
          opacity: 1;
        }
        100% {
          transform: translate(-50%, -50%) scale(0.8) translateY(-80px);
          opacity: 0;
        }
      }
    `;

    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  return null;
}
